import { HttpService } from "./https.service";
import { BASE_URL } from "../../config/config";

const http = new HttpService();

// Client for the influencer-facing CashKaro/Trackier affiliate module.
// Only ever talks to our own backend (/cashkaro/*) — the Trackier apiKey and
// all sub_id attribution logic live server-side, never in this bundle.
export class CashkaroService {
  static async getCampaigns(params: {
    influencer_id: number;
    search?: string;
    category?: string;
    page?: number;
    page_size?: number;
  }): Promise<any> {
    const query = new URLSearchParams(
      Object.entries(params).reduce((acc: Record<string, string>, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") acc[key] = String(value);
        return acc;
      }, {})
    ).toString();
    return http.getRequest(`${BASE_URL}/cashkaro/campaigns?${query}`, null);
  }

  static async getCampaignDetail(campaignId: number | string, influencerId: number): Promise<any> {
    return http.getRequest(`${BASE_URL}/cashkaro/campaigns/${campaignId}?influencer_id=${influencerId}`, null);
  }

  // NEW — brand-facing, unscoped-by-access-status campaign search, used only to let a
  // brand identify which Trackier campaign is theirs so it can be linked to their
  // Storefront (see StorefrontService.linkCashkaroCampaign). Backend endpoint TBD.
  static async searchCampaignsForBrand(params: { brand_id: number; search?: string; page?: number; page_size?: number }): Promise<any> {
    const query = new URLSearchParams(
      Object.entries(params).reduce((acc: Record<string, string>, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") acc[key] = String(value);
        return acc;
      }, {})
    ).toString();
    return http.getRequest(`${BASE_URL}/cashkaro/campaigns/brand-search?${query}`, null);
  }

  static async requestCampaignAccess(campaignId: number | string, influencerId: number): Promise<any> {
    return http.postRequest(`${BASE_URL}/cashkaro/campaigns/${campaignId}/access`, { influencer_id: influencerId }, null);
  }

  // Redirect on our own domain instead of the raw Trackier tracking_url —
  // deliberately not under /cashkaro/* so the URL itself doesn't reveal which
  // affiliate module generated it. See GET /affiliate/:click_token.
  static clickUrl(clickToken: string): string {
    return `${BASE_URL}/affiliate/${clickToken}`;
  }

  static async getCategories(): Promise<any> {
    return http.getRequest(`${BASE_URL}/cashkaro/categories`, null);
  }

  static async getCoupons(campaignId?: number | string): Promise<any> {
    const query = campaignId ? `?campaign_id=${campaignId}` : "";
    return http.getRequest(`${BASE_URL}/cashkaro/coupons${query}`, null);
  }

  static async getDeals(): Promise<any> {
    return http.getRequest(`${BASE_URL}/cashkaro/deals`, null);
  }

  static async generateLink(params: {
    influencer_id: number;
    campaign_id: number;
    target_url: string;
    // Display-only, used for the "My Links" table — the backend no longer
    // caches campaign titles locally (reads are live from Trackier now), so
    // the caller supplies it from what it's already showing on screen.
    campaign_title?: string;
    label?: string;
    // Set when the influencer picked a specific product from a brand's linked
    // Storefront picker — mutually exclusive with campaign_product_id below.
    storefront_product_id?: number;
    // Set when the influencer picked one of their OWN added products (see
    // addCampaignProduct) — always private to them, independent of any brand
    // storefront link. Mutually exclusive with storefront_product_id.
    campaign_product_id?: number;
    // Needed so the backend can resolve/register the influencer's cctm_id via
    // the shared registry (one id per influencer, shared with Storefront/Bio) —
    // used to build the link's p1/p2/p3 + composite source tracking params.
    account_id?: number;
    user_id?: number;
    // Set when this call is only to get a real tracked link to silently open in a
    // new tab (e.g. CashkaroCampaignDetail's "Add a product I found") — not
    // something the influencer explicitly asked to generate. Skips saving it to
    // cashkaro_links, so it never shows up in My Links.
    skip_history?: boolean;
  }): Promise<any> {
    return http.postRequest(`${BASE_URL}/cashkaro/links`, params, null);
  }

  // Influencer adds a product they found themselves (by browsing the brand's real
  // site through a silently-generated tracked link) — always private to them,
  // independent of whether a brand has linked a Storefront to this campaign at all.
  static async addCampaignProduct(params: {
    campaign_id: number;
    influencer_id: number;
    product_title: string;
    product_description?: string;
    product_url: string;
    image_url?: string;
    price?: number;
    currency?: string;
    category?: string;
  }): Promise<any> {
    return http.postRequest(`${BASE_URL}/cashkaro/campaign-products`, params, null);
  }

  static async getMyLinks(influencerId: number, campaignId?: number | string): Promise<any> {
    const query = campaignId ? `?influencer_id=${influencerId}&campaign_id=${campaignId}` : `?influencer_id=${influencerId}`;
    return http.getRequest(`${BASE_URL}/cashkaro/links${query}`, null);
  }

  // Cross-post an already-generated Affiliate Deals link onto the influencer's
  // own Zap Storefront / Zap Link in Bio page. Reuses those features' shared
  // click-tracking (click_token -> cct_clicks), so nothing new to track here.
  static async addLinkToStorefront(params: { link_id: number; influencer_id: number; account_id: number }): Promise<any> {
    return http.postRequest(`${BASE_URL}/cashkaro/links/${params.link_id}/add-to-storefront`, params, null);
  }

  static async addLinkToBio(params: { link_id: number; influencer_id: number; account_id: number }): Promise<any> {
    return http.postRequest(`${BASE_URL}/cashkaro/links/${params.link_id}/add-to-bio`, params, null);
  }

  static async getEarnings(params: {
    influencer_id: number;
    start?: string;
    end?: string;
    status?: "approved" | "pending" | "cancelled" | "rejected";
    group_by?: "overall" | "daily" | "campaign" | "geo";
  }): Promise<any> {
    const query = new URLSearchParams(
      Object.entries(params).reduce((acc: Record<string, string>, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") acc[key] = String(value);
        return acc;
      }, {})
    ).toString();
    return http.getRequest(`${BASE_URL}/cashkaro/earnings?${query}`, null);
  }
}
